// src/components/Navbar.js
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Globe, 
  Users, 
  Zap, 
  Settings, 
  MessageCircle, 
  Menu, 
  X, 
  LogOut,
  Wallet
} from 'lucide-react';
import { signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import './Navbar.css';

const Navbar = ({ 
  user, 
  userProfile, 
  setUser, 
  setUserProfile, 
  setIsNicknameSetupOpen, 
  openChatWindow 
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const location = useLocation();

  const menuItems = [
    { id: 'home', label: '홈', icon: Globe, path: '/' },
    { id: 'community', label: '커뮤니티', icon: Users, path: '/community' },
    { id: 'technology', label: '기술', icon: Zap, path: '/technology' },
    { id: 'chat', label: '실시간 채팅', icon: MessageCircle, path: '/chat' },
    { id: 'utils', label: '유틸리티', icon: MessageCircle, path: '/Utils' },
    { id: 'wallet', label: '지갑', icon: Wallet, path: '/wallet' },
    { id: 'admin', label: '관리', icon: MessageCircle, path: '/admin-menu' },
    // { id: 'additional-services', label: '부가서비스', icon: Settings, path: '/additional-services' }
  ];

  // <button 
  //             onClick={() => navigate('/admin-menu')}
  //             className="hero-btn admin-highlight"
  //           >
  //             <ShieldCheck className="hero-btn-icon" />
  //             <span>관리</span>
  //           </button>

  // 구글 로그인
  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    try {
      googleProvider.setCustomParameters({
        prompt: 'select_account' // 항상 계정 선택 화면 표시
      });

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // 기존 사용자 프로필 확인
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // 새 사용자인 경우 닉네임 설정 팝업 열기
        setIsNicknameSetupOpen(true);
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      alert('로그인 중 오류가 발생했습니다.');
    } finally {
      setAuthLoading(false);
    }
  };

  // 로그아웃
  const handleLogout = async () => {
    try {
      // Firebase 로그아웃
      await signOut(auth);
      
      // Google 세션까지 종료
      // window.open('https://accounts.google.com/Logout', '_blank', 'width=500,height=600');
    
      // 상태 초기화 
      setUser(null);
      setUserProfile(null);
      setIsNicknameSetupOpen(false);
      
      // localStorage 완전 삭제
      localStorage.clear();
      sessionStorage.clear();
      
      // IndexedDB 삭제 (Firebase가 사용할 수 있는 저장소)
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          await Promise.all(
            databases
              .filter(db => db.name && db.name.includes('firebase'))
              .map(db => {
                return new Promise((resolve, reject) => {
                  const deleteReq = indexedDB.deleteDatabase(db.name);
                  deleteReq.onsuccess = () => resolve();
                  deleteReq.onerror = () => reject(deleteReq.error);
                });
              })
          );
        } catch (indexedDBError) {
          console.warn('IndexedDB 삭제 중 오류:', indexedDBError);
        }
      }
      
      // 브라우저 캐시 관련 헤더 설정 (가능한 경우)
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        } catch (cacheError) {
          console.warn('캐시 삭제 중 오류:', cacheError);
        }
      }
      
      console.log('로그아웃 완료 - 모든 저장된 데이터가 삭제되었습니다.');
      
    } catch (error) {
      console.error('로그아웃 오류:', error);
      localStorage.clear();
      sessionStorage.clear();
    }
  };

  // 채팅 열기 (로그인 및 닉네임 확인)
  const handleChatClick = () => {
    if (!user) {
      handleGoogleLogin();
      return;
    }

    if (!userProfile?.nickname) {
      setIsNicknameSetupOpen(true);
      return;
    }

    // 새 창에서 채팅 열기
    openChatWindow();

  };

  // 월드랜드 지갑 열기
  const handleWalletClick = () => {
    // 모바일 디바이스 감지 함수
    const isMobileDevice = () => {
      const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const mobileScreenSize = window.innerWidth <= 768;
      return mobileUserAgent || mobileScreenSize;
    };

    // PWA 환경 감지 함수
    const isPWAMode = () => {
      return window.matchMedia('(display-mode: standalone)').matches || 
             window.navigator.standalone === true || 
             document.referrer.includes('android-app://');
    };

    // 디바이스에 따른 윈도우 기능 설정
    const getWindowFeatures = () => {
      if (isMobileDevice()) {
        const screenWidth = window.screen.width;
        let screenHeight = window.screen.height;
        
        if (isPWAMode()) {
          screenHeight = window.screen.height + 100;
          return `width=${screenWidth},height=${screenHeight},fullscreen=yes,resizable=no,scrollbars=no,status=no,menubar=no,toolbar=no,location=no,directories=no,titlebar=no`;
        } else {
          screenHeight = window.screen.height;
          return `width=${screenWidth},height=${screenHeight},fullscreen=yes,resizable=no,scrollbars=no,status=no,menubar=no,toolbar=no,location=no,directories=no,titlebar=no`;
        }
      } else {
        // 데스크톱에서는 고정 크기 (스마트폰 사이즈)
        return 'width=375,height=667,addressbar=no,resizable=no,scrollbars=no,status=no,menubar=no,toolbar=no,location=no,directories=no,titlebar=no,chrome=no';
      }
    };

    const windowFeatures = getWindowFeatures();
    
    console.log('Opening wallet window with features:', windowFeatures);
    console.log('Is mobile device:', isMobileDevice());
    console.log('Is PWA mode:', isPWAMode());

    const walletWindow = window.open('https://wallet.worldlandcafe.com', 'WorldLandWallet', windowFeatures);
    
    if (!walletWindow) {
      alert('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
    }
  }; 

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-brand">
          <Link to="/" className="brand-link">
            <div className="brand-logo">
              <img 
                src="/worldland-logo-144x144.png" 
                alt="WorldLand Logo"
                className="logo-image"
              />
            </div>
            <span className="brand-text">WorldLand Cafe</span>
          </Link>
        </div>
        
        {/* Desktop Menu */}
        <div className="navbar-menu-desktop">
          {menuItems.map((item) => (
            item.id === 'chat' ? (
              <button
                key={item.id}
                onClick={handleChatClick}
                className="navbar-item navbar-chat-btn"
              >
                <item.icon className="navbar-icon" />
                <span>{item.label}</span>
              </button>
            ) : item.id === 'wallet' ? (
              <button
                key={item.id}
                onClick={handleWalletClick}
                className="navbar-item navbar-wallet-btn"
              >
                <item.icon className="navbar-icon" />
                <span>{item.label}</span>
              </button>
            ) : (
              <Link
                key={item.id}
                to={item.path}
                className={`navbar-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon className="navbar-icon" />
                <span>{item.label}</span>
              </Link>
            )
          ))}
          
          {/* 사용자 메뉴 */}
          {user ? (
            <div className="navbar-user-menu">
              <div className="user-info">
                <img 
                  src={user.photoURL} 
                  alt="프로필" 
                  className="user-avatar"
                />
                <span className="user-name">
                  {userProfile?.nickname || user.displayName}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="logout-btn"
                title="로그아웃"
              >
                <LogOut className="navbar-icon" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleGoogleLogin}
              disabled={authLoading}
              className="login-btn"
            >
              {authLoading ? '로그인 중...' : '로그인'}
            </button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="navbar-icon" /> : <Menu className="navbar-icon" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="navbar-menu-mobile">
          <div className="mobile-menu-items">
            {menuItems.map((item) => (
              item.id === 'chat' ? (
                <button
                  key={item.id}
                  onClick={() => {
                    handleChatClick();
                    setIsMenuOpen(false);
                  }}
                  className="mobile-menu-item mobile-chat-btn"
                >
                  <item.icon className="navbar-icon" />
                  <span>{item.label}</span>
                </button>
              ) : item.id === 'wallet' ? (
                <button
                  key={item.id}
                  onClick={() => {
                    handleWalletClick();
                    setIsMenuOpen(false);
                  }}
                  className="mobile-menu-item mobile-wallet-btn"
                >
                  <item.icon className="navbar-icon" />
                  <span>{item.label}</span>
                </button>
              ) : (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`mobile-menu-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <item.icon className="navbar-icon" />
                  <span>{item.label}</span>
                </Link>
              )
            ))}
            
            {/* 모바일 사용자 메뉴 */}
            {user ? (
              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <img 
                    src={user.photoURL} 
                    alt="프로필" 
                    className="user-avatar"
                  />
                  <span className="user-name">
                    {userProfile?.nickname || user.displayName}
                  </span>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="mobile-logout-btn"
                >
                  <LogOut className="navbar-icon" />
                  <span>로그아웃</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  handleGoogleLogin();
                  setIsMenuOpen(false);
                }}
                disabled={authLoading}
                className="mobile-login-btn"
              >
                {authLoading ? '로그인 중...' : '로그인'}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;